<template>
    <component :is="hideCardWrapper ? 'div' : Card">
        <component :is="hideCardWrapper ? 'div' : CardHeader" v-if="!hideCardWrapper">
            <div class="flex items-center gap-2">
                <CreditCardIcon class="size-4 text-muted-foreground" />
                <CardTitle class="text-sm font-medium text-primary">
                    Payment method
                </CardTitle>
            </div>
        </component>

        <component :is="hideCardWrapper ? 'div' : CardContent">
            <!-- Loading -->
            <div v-if="pmLoading" class="flex h-[150px] items-center justify-center">
                <div class="size-8 animate-spin rounded-full border-2 border-new-neutral-300 border-t-new-neutral-900"></div>
            </div>

            <!-- Empty state -->
            <div v-else-if="!paymentMethods.length && !showAddForm" class="flex flex-col items-center justify-center py-8 gap-1.5">
                <svg width="40" height="40" viewBox="0 0 80 80" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M10 80H0V60.002H10V80ZM29.999 80H19.999V60.002H29.999V80ZM59.999 80H49.999V60.002H59.999V80ZM79.999 80H69.999V60.002H79.999V80ZM19.999 50H29.999V30.002H49.999V50H59.999V30.002H69.999V50H79.999V60H69.999V50.002H59.999V60H49.999V50.002H29.999V60H19.999V50.002H10V60H0V50H9.99902V30.002H19.999V50ZM10 20H19.999V10.002H59.999V20H69.999V10.002H79.999V20.002H69.999V30H59.999V20.002H19.999V30H9.99902V20.002H0V10.002H10V20ZM10 10H0V0H10V10ZM79.999 10H69.999V0H79.999V10Z" fill="#F5F5F5"/>
                </svg>
                <p class="text-sm font-semibold text-foreground">No payment methods added</p>
                <button
                    class="text-xs font-semibold text-new-blue-500 hover:text-new-blue-600"
                    @click="showAddForm = true"
                >
                    Add payment method
                </button>
            </div>

            <!-- Payment methods list -->
            <div v-else-if="paymentMethods.length && !showAddForm" class="space-y-3">
                <div
                    v-for="method in paymentMethods"
                    :key="method.token || method.id"
                    class="flex items-center justify-between border border-new-neutral-200 rounded-none p-4"
                    :class="{ 'cursor-pointer hover:bg-new-neutral-50': !method.default }"
                    @click="!method.default && confirmSetDefault(method)"
                >
                    <div class="flex items-center gap-3">
                        <img
                            v-if="method.imageUrl"
                            :src="method.imageUrl"
                            class="h-6 object-contain"
                        />
                        <div>
                            <p class="text-sm text-foreground">
                                <template v-if="method.last4">•••• {{ method.last4 }}</template>
                                <template v-else-if="method.email">{{ method.email }}</template>
                            </p>
                            <p v-if="method.expirationDate" class="text-xs text-muted-foreground">
                                Expires {{ method.expirationDate }}
                            </p>
                        </div>
                    </div>
                    <div class="flex items-center gap-2">
                        <Badge v-if="method.default" variant="success">Default</Badge>
                        <button
                            v-if="!method.default"
                            class="text-xs text-destructive hover:opacity-80"
                            @click.stop="confirmDelete(method)"
                        >
                            Delete
                        </button>
                    </div>
                </div>

                <button
                    class="text-sm font-semibold text-new-blue-500 hover:text-new-blue-600"
                    @click="showAddForm = true"
                >
                    + Add another payment method
                </button>
            </div>

            <!-- Add card form -->
            <div v-if="showAddForm" class="space-y-5">
                <h3 class="text-lg font-medium text-foreground">Paying with card</h3>

                <!-- Loading skeleton for hosted fields -->
                <div v-show="hostedFieldsLoading" class="space-y-5 max-w-[400px]">
                    <div>
                        <div class="mb-2 h-4 w-24 rounded bg-new-neutral-200 animate-pulse"></div>
                        <div class="h-10 w-full rounded-none bg-new-neutral-200 animate-pulse"></div>
                    </div>
                    <div class="flex gap-4">
                        <div class="flex-1 max-w-[120px]">
                            <div class="mb-2 h-4 w-16 rounded bg-new-neutral-200 animate-pulse"></div>
                            <div class="h-10 w-full rounded-none bg-new-neutral-200 animate-pulse"></div>
                        </div>
                        <div class="flex-1 max-w-[120px]">
                            <div class="mb-2 h-4 w-20 rounded bg-new-neutral-200 animate-pulse"></div>
                            <div class="h-10 w-full rounded-none bg-new-neutral-200 animate-pulse"></div>
                        </div>
                    </div>
                </div>

                <!-- Hosted fields (Braintree/Stripe inject iframes into these divs) -->
                <div v-show="!hostedFieldsLoading" class="space-y-5 max-w-[400px]">
                    <div>
                        <div class="flex items-center justify-between mb-2">
                            <Label class="text-sm font-medium text-foreground">Card number</Label>
                            <div class="flex gap-1">
                                <img src="https://d1vyenbk43iobe.cloudfront.net/application/images/payments/visa.svg" class="h-5" alt="Visa" />
                            </div>
                        </div>
                        <div
                            id="cardNumber"
                            class="h-10 w-full rounded-none border border-new-neutral-200 bg-white px-3 py-2 text-sm shadow-sm"
                        ></div>
                    </div>

                    <div v-if="!isStripeEnabled">
                        <Label class="text-sm font-medium text-foreground mb-2">Name on card</Label>
                        <div
                            id="cardholderName"
                            class="h-10 w-full rounded-none border border-new-neutral-200 bg-white px-3 text-sm shadow-sm"
                        ></div>
                    </div>

                    <div class="flex gap-4">
                        <div class="flex-1 max-w-[120px]">
                            <Label class="text-sm font-medium text-foreground mb-2">Expiry date</Label>
                            <div
                                id="cardExpirationDate"
                                class="h-10 w-full rounded-none border border-new-neutral-200 bg-white px-3 py-2 text-sm shadow-sm"
                            ></div>
                        </div>
                        <div class="flex-1 max-w-[120px]">
                            <Label class="text-sm font-medium text-foreground mb-2">Security code</Label>
                            <div
                                id="cardCvv"
                                class="h-10 w-full rounded-none border border-new-neutral-200 bg-white px-3 py-2 text-sm shadow-sm"
                            ></div>
                        </div>
                    </div>
                </div>

                <div class="flex items-center gap-3">
                    <Button :disabled="cardLoading" @click="saveCard">
                        Add credit card
                    </Button>
                    <Button variant="outline" @click="cancelAdd">
                        Cancel
                    </Button>
                </div>

                <!-- Cardholder additional info checkbox -->
                <label class="flex items-center gap-2 cursor-pointer">
                    <Checkbox v-model="cardAdditionalInfoEnabled" />
                    <span class="text-sm text-foreground">Provide us with more information about the cardholder</span>
                </label>

                <!-- Cardholder additional info form -->
                <div v-if="cardAdditionalInfoEnabled" class="rounded-md border border-new-neutral-200 bg-new-neutral-50 p-6 space-y-5 max-w-[400px]">
                    <div class="flex gap-4">
                        <div class="flex-1">
                            <Label class="text-sm font-medium text-foreground">First name <span class="text-new-red-500">*</span></Label>
                            <Input v-model="cardInfo.given_name" class="mt-2" />
                        </div>
                        <div class="flex-1">
                            <Label class="text-sm font-medium text-foreground">Last name <span class="text-new-red-500">*</span></Label>
                            <Input v-model="cardInfo.surname" class="mt-2" />
                        </div>
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">E-mail address</Label>
                        <Input v-model="cardInfo.email" type="email" class="mt-2" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Phone number</Label>
                        <Input v-model="cardInfo.phone_number" class="mt-2" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Country <span class="text-new-red-500">*</span></Label>
                        <Select v-model="cardInfo.country_code">
                            <SelectTrigger class="mt-2">
                                <SelectValue placeholder="Select country" />
                            </SelectTrigger>
                            <SelectContent :body-lock="false">
                                <SelectItem
                                    v-for="country in countries"
                                    :key="country.value"
                                    :value="country.value"
                                >
                                    {{ country.title }}
                                </SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Address <span class="text-new-red-500">*</span></Label>
                        <Input v-model="cardInfo.street_address" placeholder="Address" class="mt-2" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">Address (optional)</Label>
                        <Input v-model="cardInfo.extended_address" class="mt-2" />
                    </div>
                    <div>
                        <Label class="text-sm font-medium text-foreground">City <span class="text-new-red-500">*</span></Label>
                        <Input v-model="cardInfo.locality" class="mt-2" />
                    </div>
                    <div class="flex gap-4">
                        <div class="flex-1">
                            <Label class="text-sm font-medium text-foreground">Region / State</Label>
                            <Input v-model="cardInfo.region" class="mt-2" />
                        </div>
                        <div class="flex-1">
                            <Label class="text-sm font-medium text-foreground">ZIP code</Label>
                            <Input v-model="cardInfo.postal_code" placeholder="Postal code" class="mt-2" />
                        </div>
                    </div>
                </div>
            </div>
        </component>
    </component>

    <!-- Set Default Confirmation -->
    <Dialog :open="!!pendingDefault" @update:open="pendingDefault = null">
        <DialogContent class="sm:max-w-sm">
            <DialogHeader>
                <DialogTitle>Set as default</DialogTitle>
            </DialogHeader>
            <p class="text-sm text-muted-foreground">
                Are you sure you want to set this card as your default payment method?
            </p>
            <DialogFooter class="mt-4">
                <Button variant="outline" @click="pendingDefault = null">Cancel</Button>
                <Button @click="setDefault(pendingDefault)">Confirm</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>

    <!-- Delete Confirmation -->
    <Dialog :open="!!pendingDelete" @update:open="pendingDelete = null">
        <DialogContent class="sm:max-w-sm">
            <DialogHeader>
                <DialogTitle>Delete payment method</DialogTitle>
            </DialogHeader>
            <p class="text-sm text-muted-foreground">
                Are you sure you want to delete this payment method? This action cannot be undone.
            </p>
            <DialogFooter class="mt-4">
                <Button variant="outline" @click="pendingDelete = null">Cancel</Button>
                <Button variant="destructive" @click="deleteMethod(pendingDelete)">Delete</Button>
            </DialogFooter>
        </DialogContent>
    </Dialog>
</template>

<script lang="ts" setup>
import { ref, onMounted, onUnmounted, computed, PropType } from 'vue';
import { toast } from 'vue-sonner';
import { CreditCard as CreditCardIcon } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { useBraintreeClient } from '@/composables/useBraintree';
import { useStripeClient } from '@/composables/useStripe';
import useFeatures from '@/composables/useFeatures';
import BillingAddress from '@/types/Subscription/BillingAddress';
import ITeamCardAdditionalInformation from '@/types/Members/ITeamCardAdditionalInformation';

const props = defineProps({
    billingAddress: {
        type: Object as PropType<BillingAddress>,
        required: true,
    },
    countries: {
        type: Array as PropType<Array<{ value: string; title: string }>>,
        default: () => [],
    },
    hideCardWrapper: {
        type: Boolean,
        default: false,
    },
    type: {
        type: String as PropType<'pro' | 'plugin'>,
        default: 'plugin',
    },
});

const emit = defineEmits<{
    selectPaymentMethod: [method: unknown];
}>();

const { teamFeatureDisabled } = useFeatures();
const isStripeEnabled = !teamFeatureDisabled('stripe');

const {
    createClient: braintreeCreateClient,
    teardownClient: braintreeTeardownClient,
    verifyCard: braintreeVerifyCard,
    addPaymentMethod: braintreeAddPaymentMethod,
    getPaymentMethods: braintreeGetPaymentMethods,
    setDefaultPaymentMethod: braintreeSetDefault,
    deletePaymentMethod: braintreeDelete,
} = useBraintreeClient(props.type);

const {
    addPaymentMethod: stripeAddPaymentMethod,
    createHostedFields,
    getPaymentMethods: stripeGetPaymentMethods,
    setDefaultPaymentMethod: stripeSetDefault,
    deletePaymentMethod: stripeDelete,
} = useStripeClient(props.type);

const pmLoading = ref(true);
const paymentMethods = ref<any[]>([]);
const showAddForm = ref(false);
const hostedFieldsLoading = ref(true);
const cardLoading = ref(false);
const cardAdditionalInfoEnabled = ref(false);

const cardInfo = ref<ITeamCardAdditionalInformation>({
    given_name: props.billingAddress.name?.split(' ')[0] || '',
    surname: props.billingAddress.name?.split(' ')[1] || '',
    phone_number: '',
    street_address: props.billingAddress.street || '',
    extended_address: '',
    locality: props.billingAddress.city || '',
    region: '',
    postal_code: props.billingAddress.postal_code || '',
    country_code: props.billingAddress.country !== 'EL' ? (props.billingAddress.country || '') : 'GR',
    email: props.billingAddress.email || '',
});

const cardAdditionalInfo = computed(() => {
    return cardAdditionalInfoEnabled.value ? cardInfo.value : null;
});

onMounted(async () => {
    await fetchPaymentMethods();
});

onUnmounted(async () => {
    await braintreeTeardownClient();
});

const fetchPaymentMethods = async () => {
    pmLoading.value = true;
    try {
        const response = isStripeEnabled
            ? await stripeGetPaymentMethods()
            : await braintreeGetPaymentMethods();

        if (response.data.success === false) {
            throw new Error(response.data.message ?? 'Failed to fetch payment methods');
        }

        paymentMethods.value = response.data.data || [];

        const defaultMethod = paymentMethods.value.find((m: any) => m.default);
        if (defaultMethod) {
            emit('selectPaymentMethod', defaultMethod);
        }
    } catch (e: unknown) {
        console.error('[PaymentMethodCard] fetchPaymentMethods failed:', e);
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        const message = err.response?.data?.message ?? err.message ?? 'Failed to fetch payment methods';
        toast.error(message);
    } finally {
        pmLoading.value = false;
    }
};

const initHostedFields = async () => {
    hostedFieldsLoading.value = true;
    try {
        if (isStripeEnabled) {
            await createHostedFields();
        } else {
            await braintreeCreateClient({ enableHostedFields: true });
        }
    } catch (e) {
        console.error('Failed to init hosted fields:', e);
    } finally {
        hostedFieldsLoading.value = false;
    }
};

// Watch showAddForm to init hosted fields when form opens
watch(showAddForm, async (val) => {
    if (val) {
        await nextTick();
        await initHostedFields();
    }
});

const saveCard = async () => {
    cardLoading.value = true;
    try {
        let newPaymentMethodId: string | null = null;

        if (isStripeEnabled) {
            newPaymentMethodId = await stripeAddPaymentMethod({
                name: (cardInfo.value.given_name + ' ' + cardInfo.value.surname).trim(),
                country: cardInfo.value.country_code ?? '',
                postal_code: cardInfo.value.postal_code ?? '',
            });
        } else {
            const response = await braintreeVerifyCard({
                card_additional_information: cardAdditionalInfo.value,
            });
            await braintreeAddPaymentMethod({
                nonce: response.nonce,
                card_additional_information: cardAdditionalInfo.value,
            });
        }

        showAddForm.value = false;

        // Stripe attaches the payment method to the customer, then our backend asks
        // Stripe for the customer's payment-method list. There can be a brief window
        // where the new card hasn't propagated yet — poll a few times before giving up
        // so the user doesn't see "Payment method added" with an empty list.
        await waitForCard(newPaymentMethodId);
        toast.success('Payment method added');
    } catch (e) {
        toast.error(
            e instanceof Error ? e.message : 'Failed to add payment method'
        );
    } finally {
        cardLoading.value = false;
    }
};

const waitForCard = async (expectedId: string | null) => {
    for (let attempt = 0; attempt < 5; attempt++) {
        await fetchPaymentMethods();
        if (!expectedId) return;
        if (paymentMethods.value.some((m: { id?: string; token?: string }) => m.id === expectedId || m.token === expectedId)) {
            return;
        }
        await new Promise((resolve) => setTimeout(resolve, 400));
    }
};

const pendingDefault = ref<any>(null);
const pendingDelete = ref<any>(null);

const confirmSetDefault = (method: any) => {
    pendingDefault.value = method;
};

const confirmDelete = (method: any) => {
    pendingDelete.value = method;
};

const setDefault = async (method: any) => {
    if (!method) return;
    const captured = method;
    pendingDefault.value = null;
    try {
        const token = captured.token || captured.id;
        const response = isStripeEnabled
            ? await stripeSetDefault(token)
            : await braintreeSetDefault(token);

        if (response?.data?.success === false) {
            throw new Error(response.data.message ?? 'Failed to set default payment method');
        }

        toast.success('Default payment method updated');
        await fetchPaymentMethods();
    } catch (e: unknown) {
        console.error('[PaymentMethodCard] setDefault failed:', e);
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        const message = err.response?.data?.message ?? err.message ?? 'Failed to set default payment method';
        toast.error(message);
    }
};

const deleteMethod = async (method: any) => {
    if (!method) return;
    const captured = method;
    pendingDelete.value = null;
    try {
        const token = captured.token || captured.id;
        const response = isStripeEnabled
            ? await stripeDelete(token)
            : await braintreeDelete(token);

        if (response?.data?.success === false) {
            throw new Error(response.data.message ?? 'Failed to delete payment method');
        }

        toast.success('Payment method deleted');
        await fetchPaymentMethods();
    } catch (e: unknown) {
        console.error('[PaymentMethodCard] deleteMethod failed:', e);
        const err = e as { response?: { data?: { message?: string } }; message?: string };
        const message = err.response?.data?.message ?? err.message ?? 'Failed to delete payment method';
        toast.error(message);
    }
};

const cancelAdd = async () => {
    showAddForm.value = false;
    await braintreeTeardownClient();
};

defineExpose({ refresh: fetchPaymentMethods });
</script>

<style>
div.braintree-hosted-fields-focused {
    border-color: var(--color-new-neutral-900) !important;
}

div.braintree-hosted-fields-invalid {
    border-color: var(--color-new-red-500) !important;
}
</style>
